import React, { useCallback } from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../../shared/ui/kit/utils';
import { FILTER_CONFIG } from '../constants';
import type { FilterType, MessageStats } from '../types';

interface FilterBarProps {
    activeFilter: FilterType;
    onFilterChange: (filter: FilterType) => void;
    search: string;
    onSearchChange: (search: string) => void;
    stats: MessageStats;
}

export const FilterBar: React.FC<FilterBarProps> = ({
    activeFilter,
    onFilterChange,
    search,
    onSearchChange,
    stats,
}) => {
    const getBadgeCount = useCallback((filter: FilterType): number | undefined => {
        switch (filter) {
            case 'unread': return stats.unread;
            case 'critical': return stats.critical;
            case 'warning': return stats.warning;
            default: return undefined;
        }
    }, [stats]);

    const filters: FilterType[] = ['all', 'unread', 'critical', 'warning', 'info', 'archived'];

    return (
        <div className="px-[18px] pb-4 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
                {filters.map(filter => {
                    const config = FILTER_CONFIG[filter];
                    const isActive = activeFilter === filter;
                    const badgeCount = getBadgeCount(filter);

                    return (
                        <motion.button
                            key={filter}
                            onClick={() => onFilterChange(filter)}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className={cn(
                                'relative px-3 py-1.5 rounded-full text-[12px] font-medium border transition-all duration-200',
                                isActive
                                ? filter === 'critical'
                                    ? 'bg-rose-500/10 border-rose-500/30 text-rose-700 dark:text-rose-300'
                                    : filter === 'warning'
                                    ? 'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-300'
                                    : 'bg-gray-900 dark:bg-gray-100 border-gray-900 dark:border-gray-100 text-white dark:text-gray-900'
                                : 'bg-white dark:bg-white/5 border-black/[.07] dark:border-white/[.07] text-gray-600 dark:text-gray-400 hover:border-black/10 dark:hover:border-white/10'
                            )}
                        >
                            <span className="flex items-center gap-1.5">
                                {config.badge && (
                                    <span className={cn(
                                        'w-1.5 h-1.5 rounded-full',
                                        config.badge === 'critical' ? 'bg-rose-500' :
                                        config.badge === 'warning' ? 'bg-amber-500' :
                                        'bg-blue-500'
                                    )} />
                                )}
                                {config.label}
                                {badgeCount !== undefined && badgeCount > 0 && (
                                    <span className={cn(
                                        'ml-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold flex items-center justify-center',
                                        filter === 'critical' ? 'bg-rose-500 text-white' :
                                        filter === 'warning' ? 'bg-amber-500 text-white' :
                                        'bg-gray-600 dark:bg-gray-400 text-white'
                                    )}>
                                        {badgeCount > 99 ? '99+' : badgeCount}
                                    </span>
                                )}
                            </span>
                            {isActive && (
                                <motion.div
                                    layoutId="activeFilter"
                                    className="absolute inset-0 rounded-full border-2 border-current opacity-20"
                                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                                />
                            )}
                        </motion.button>
                    );
                })}
            </div>

            <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <circle cx="11" cy="11" r="8"/>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                </div>
                <input
                    type="text"
                    placeholder="Поиск по сообщениям..."
                    value={search}
                    onChange={(e) => onSearchChange(e.target.value)}
                    className={cn(
                        'w-full pl-9 pr-8 py-2 rounded-lg',
                        'bg-white dark:bg-white/5',
                        'border border-black/[.07] dark:border-white/[.07]',
                        'text-[13px] text-gray-700 dark:text-gray-300',
                        'placeholder:text-gray-400',
                        'focus:outline-none focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/10',
                        'transition-all duration-150'
                    )}
                />
                {search && (
                    <button
                        onClick={() => onSearchChange('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <line x1="18" y1="6" x2="6" y2="18"/>
                            <line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                )}
            </div>
        </div>
    );
};